import { useCallback, useState, type ReactNode } from 'react';
import { Accept, useDropzone } from 'react-dropzone';
import { Upload, X } from 'lucide-react';

interface FileUploadZoneProps {
  filePreviewUrl: string;
  setFile: (file: File | null) => void;
  setPreviewUrl: (url: string) => void;
  maxSizeBytes: number;
  accept?: Accept;
  children?: ReactNode;
  onClear?: () => void;
  containerHeight?: string;
  recommendedSize?: string;
  maxWidth?: string;
}

function FileUploadZone({
  filePreviewUrl,
  setFile,
  setPreviewUrl,
  maxSizeBytes,
  accept,
  onClear,
  containerHeight = 'h-48',
  recommendedSize = '256x256px',
  maxWidth = 'sm:w-[350px] w-full'
}: Readonly<FileUploadZoneProps>): React.ReactElement {
  const [errorMessage, setErrorMessage] = useState('');

  const imageAccept: Accept = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/gif': ['.gif'],
    'image/webp': ['.webp']
  };

  const effectiveAccept = accept ?? imageAccept;

  const getReadableFileTypes = (acc: Accept): string => {
    const extensions = Object.values(acc).flat();
    const labelMap: Record<string, string> = {
      '.jpg': 'JPEG',
      '.jpeg': 'JPEG',
      '.png': 'PNG',
      '.gif': 'GIF',
      '.webp': 'WebP'
    };

    const readable = extensions
      .map((ext) => labelMap[ext.toLowerCase()] || ext.toUpperCase())
      .filter((v, i, arr) => arr.indexOf(v) === i)
      .join(', ');

    return readable;
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) {
        return;
      }

      const acceptedFile = acceptedFiles[0];

      const isValidFileType = (file: File): boolean => {
        const mimeType = file.type;
        const acceptedMimes = Object.keys(effectiveAccept);
        return acceptedMimes.some((mime) => {
          if (mime.includes('*')) {
            const baseMime = mime.split('/')[0];
            return mimeType.startsWith(baseMime);
          }
          return mimeType === mime;
        });
      };

      if (!isValidFileType(acceptedFile)) {
        const supportedTypes = getReadableFileTypes(effectiveAccept);
        setErrorMessage(
          `Unsupported file type. Supported formats: ${supportedTypes}`
        );
        return;
      }

      if (acceptedFile.size > maxSizeBytes) {
        const maxSizeMB = (maxSizeBytes / 1024 / 1024).toFixed(1);
        setErrorMessage(`File size exceeds ${maxSizeMB}MB limit`);
        return;
      }

      setErrorMessage('');
      setFile(acceptedFile);

      const previewUrl = URL.createObjectURL(acceptedFile);
      setPreviewUrl(previewUrl);
    },
    [setFile, setPreviewUrl, maxSizeBytes, effectiveAccept]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: effectiveAccept,
    maxSize: maxSizeBytes,
    maxFiles: 1,
    onDropRejected: (rejections) => {
      const firstError = rejections[0]?.errors[0];
      if (firstError) {
        setErrorMessage(firstError.message);
      }
    }
  });

  const handleRemove = (): void => {
    setFile(null);
    setPreviewUrl('');
    if (onClear) {
      onClear();
    }
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const { target } = event;
    const { files } = target;
    if (files && files.length > 0) {
      onDrop(Array.from(files));
    }
  };

  return (
    <div className={`space-y-2 ${maxWidth}`}>
      <div
        {...getRootProps()}
        className={`flex flex-col items-center justify-center border-2 border-dashed
          rounded-lg p-6 ${containerHeight} cursor-pointer hover:border-foreground/50
          transition-colors w-full`}
      >
        <input {...getInputProps()} onChange={handleFileInputChange} />

        {filePreviewUrl ? (
          <div className='relative w-full h-full'>
            <img
              src={filePreviewUrl}
              alt='Preview'
              className='w-full h-full object-contain'
            />
            <button
              className='absolute top-2 right-2 rounded-full bg-muted text-muted-foreground hover:bg-muted/80 p-1'
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
              type='button'
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <div className='flex flex-col items-center justify-center text-center'>
            <Upload className='h-10 w-10 text-muted-foreground mb-2' />
            <p className='text-sm text-muted-foreground'>
              Drag and drop an image, or click to browse
            </p>
          </div>
        )}
      </div>

      <p className='text-xs text-muted-foreground'>
        Recommended size: {recommendedSize}. Max file size:{' '}
        {(maxSizeBytes / 1024 / 1024).toFixed(1)}MB.
      </p>

      <p className='text-xs text-muted-foreground'>
        Supported formats: {getReadableFileTypes(effectiveAccept)}
      </p>

      {errorMessage && (
        <p className='text-xs text-destructive'>{errorMessage}</p>
      )}
    </div>
  );
}

export default FileUploadZone;
